import { createContext, useCallback, useContext, useMemo, useState } from "react";
import React from "react";
import { useFragment } from "react-relay";

import { noop } from "lodash";
import { graphql } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, toast, useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";

import { dispatchAutoRecordRuleAnalytics } from "../util";
import { RuleConfigurationStep } from "./RuleConfigurationStep";
import { CreationSteps, type RuleInput, type UserInput } from "./types";
import { UserSelectionStep } from "./UserSelectionStep";

import type {
	CreateAndEditModalAccount$data,
	CreateAndEditModalAccount$key,
} from "~/__generated__/CreateAndEditModalAccount.graphql";
import type {
	AutoRecordRuleFilterOrganizer,
	AutoRecordRuleLevel,
	CreateAndEditModalCreateRuleMutation,
	CreateAndEditModalCreateRuleMutation$data,
} from "~/__generated__/CreateAndEditModalCreateRuleMutation.graphql";
import type { CreateAndEditModalRule$key } from "~/__generated__/CreateAndEditModalRule.graphql";
import type {
	CreateAndEditModalUpdateRuleMutation,
	CreateAndEditModalUpdateRuleMutation$data,
	FilterAttendeeType,
} from "~/__generated__/CreateAndEditModalUpdateRuleMutation.graphql";

/**
 * Hook that returns a function that updates a given rule
 * @param rule - A rule object that contains the rule data that requires an id
 * @param refetch - A function that refetches the data after the mutation is complete
 * @returns  A callback function that updates the rule
 */
function useUpdateRuleMutation(rule: RuleInput, refetch: () => void) {
	const updateMutation = graphql`
		mutation CreateAndEditModalUpdateRuleMutation($input: UpdateAutoRecordRuleInput!) {
			updateAutoRecordRule(input: $input) {
				rule {
					id
				}
			}
		}
	`;

	const usersWithReports = rule.autoRecordRuleUsers
		.map(autoRecordRuleUser => {
			if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
				return null;
			}

			return autoRecordRuleUser.includeAllReports ? autoRecordRuleUser.user.id : null;
		})
		.filter(Boolean) as string[];

	const usersWithoutReports = rule.autoRecordRuleUsers
		.map(autoRecordRuleUser => {
			if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
				return null;
			}

			return autoRecordRuleUser.includeAllReports ? null : autoRecordRuleUser.user.id;
		})
		.filter(Boolean) as string[];

	const updateRule = useCallback(() => {
		if (!rule || !rule.id) {
			throw new Error("Rule id is required to update rule");
		}

		commitMutationPromise<CreateAndEditModalUpdateRuleMutation>(environment, {
			mutation: updateMutation,
			variables: {
				input: {
					id: rule.id,
					ruleType: rule.ruleType,
					ruleLevel: rule.ruleLevel,
					filterAttendeeType: rule.filterAttendeeType,
					filterMinimumAttendees: rule.filterMinimumAttendees,
					filterMaximumAttendees: rule.filterMaximumAttendees,
					filterOrganizer: rule.filterOrganizer,
					filterKeywordsContains: rule.filterKeywordsContains,
					filterKeywordsRestrict: rule.filterKeywordsRestrict,
					usersWithReports: rule.ruleLevel === "WORKSPACE" ? null : usersWithReports,
					usersWithoutReports: rule.ruleLevel === "WORKSPACE" ? null : usersWithoutReports,
				},
			},
		}).then((response: CreateAndEditModalUpdateRuleMutation$data) => {
			if (response?.updateAutoRecordRule?.rule) {
				toast.success(
					`${
						rule.ruleType === "RECORD"
							? "Auto-record policy saved. Policy will be active within 20 minutes."
							: "Restriction policy saved."
					}`,
				);
			} else {
				toast.error(`Failed to save ${rule.ruleType === "RECORD" ? "auto-record policy" : "restriction policy"}`);
			}
			refetch();
		});
	}, [refetch, rule, updateMutation, usersWithReports, usersWithoutReports]);

	return updateRule;
}

/**
 * Hook that returns a function that creates a rule
 * @param rule A rule object that contains the rule data and does not have an id
 * @param refetch  A function that refetches the data after the mutation is complete
 * @returns  A callback function that creates the rule
 */
function useCreateRuleMutation(rule: RuleInput, refetch: () => void) {
	const createMutation = graphql`
		mutation CreateAndEditModalCreateRuleMutation($input: CreateAutoRecordRuleInput!) {
			createAutoRecordRule(input: $input) {
				rule {
					id
				}
			}
		}
	`;

	const usersWithReports = rule.autoRecordRuleUsers
		.map(autoRecordRuleUser => {
			if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
				return null;
			}

			return autoRecordRuleUser.includeAllReports ? autoRecordRuleUser.user.id : null;
		})
		.filter(Boolean) as string[];

	const usersWithoutReports = rule.autoRecordRuleUsers
		.map(autoRecordRuleUser => {
			if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
				return null;
			}

			return autoRecordRuleUser.includeAllReports ? null : autoRecordRuleUser.user.id;
		})
		.filter(Boolean) as string[];

	const createRule = useCallback(() => {
		commitMutationPromise<CreateAndEditModalCreateRuleMutation>(environment, {
			mutation: createMutation,
			variables: {
				input: {
					ruleType: rule.ruleType,
					ruleLevel: rule.ruleLevel,
					filterAttendeeType: rule.filterAttendeeType,
					filterOrganizer: rule.filterOrganizer,
					filterMinimumAttendees: rule.filterMinimumAttendees,
					filterMaximumAttendees: rule.filterMaximumAttendees,
					filterKeywordsContains: rule.filterKeywordsContains,
					filterKeywordsRestrict: rule.filterKeywordsRestrict,
					usersWithReports: rule.ruleLevel === "WORKSPACE" ? null : usersWithReports,
					usersWithoutReports: rule.ruleLevel === "WORKSPACE" ? null : usersWithoutReports,
				},
			},
		}).then((response: CreateAndEditModalCreateRuleMutation$data) => {
			if (response?.createAutoRecordRule?.rule) {
				toast.success(
					`${
						rule.ruleType === "RECORD"
							? "Auto-record policy added. Policy will be active within 20 minutes."
							: "Restriction policy added."
					}`,
				);
				dispatchAutoRecordRuleAnalytics(
					{ ruleType: rule.ruleType, ruleLevel: rule.ruleLevel },
					"Completed creating auto-record restriction policy",
				);
			} else {
				toast.error(`Failed to add ${rule.ruleType === "RECORD" ? "auto-record policy" : "restriction policy"}`);
			}
			refetch();
		});
	}, [refetch, rule, createMutation, usersWithReports, usersWithoutReports]);

	return createRule;
}

/**
 * Required arguments to open the CreateAndEditModal
 */
interface CreateAndEditModalArgs {
	rule?: RuleInput;
	ruleType: "RECORD" | "RESTRICT";
	refetch: () => void;
	ruleLevel?: AutoRecordRuleLevel;
	account: CreateAndEditModalAccount$key;
}

/**
 * Context that provides the state and functions to the CreateAndEditModal
 */
interface CreateAndEditModalContext {
	step: CreationSteps;
	isEditing: boolean;
	rule: RuleInput;
	hasDuplicateKeywords: boolean;
	ruleLevel: AutoRecordRuleLevel | null;
	users: UserInput[];
	account: CreateAndEditModalAccount$data;
	nextStep: () => void;
	previousStep: () => void;
	addUserInput: (user: UserInput) => void;
	removeUserInput: (index: number) => void;
	updateUserInput: (index: number, user: UserInput) => void;
	setUsers: React.Dispatch<React.SetStateAction<UserInput[]>>;
	closeModal: () => void;
	setAttendeeType: (attendeeType: FilterAttendeeType) => void;
	setFilterMinimumAttendees: (minimumAttendees?: number) => void;
	setFilterMaximumAttendees: (maximumAttendees?: number) => void;
	setKeywordsToContain: React.Dispatch<React.SetStateAction<string[]>>;
	setKeywordsToRestrict: React.Dispatch<React.SetStateAction<string[]>>;
	setRuleLevel: React.Dispatch<React.SetStateAction<AutoRecordRuleLevel>>;
	setFilterOrganizer: React.Dispatch<React.SetStateAction<AutoRecordRuleFilterOrganizer>>;
}

const CreateAndEditModalContext = createContext<CreateAndEditModalContext | undefined>(undefined);

/**
 * Hook to safely access the CreateAndEditModalContext
 */
export function useCreateAndEditModal() {
	const context = useContext(CreateAndEditModalContext);

	if (!context) {
		throw new Error("useCreateAndEditModal must be used within a CreateAndEditModalProvider");
	}

	return context;
}

/**
 * Hook that fetches a fragment from a given rule reference and returns a mutable rule input object
 * @param ruleRef a reference to the rule field that is being edited
 * @returns a mutable rule input object that can be used to edit the rule throughout the lifetime of the component
 */
function useGetRuleInput(ruleRef: CreateAndEditModalRule$key): RuleInput {
	const ruleInputFragment = graphql`
		fragment CreateAndEditModalRule on AutoRecordRule {
			id
			ruleType
			autoRecordRuleUsers {
				includeAllReports
				user {
					id
					...UserSelectionStepGetIncludesString
					...CopilotSettingsUserFilter
					...RuleConfigurationStepUserPill
					...UserSelectionStepGetIncludesString
				}
			}
			ruleLevel
			filterAttendeeType
			filterMinimumAttendees
			filterMaximumAttendees
			filterOrganizer
			filterKeywordsContains
			filterKeywordsRestrict
		}
	`;

	const rule = useFragment<CreateAndEditModalRule$key>(ruleInputFragment, ruleRef);

	let keywordsContains: string[] = [];
	if (rule.filterKeywordsContains) {
		for (const keyword of rule.filterKeywordsContains) {
			if (keyword) {
				keywordsContains.push(keyword);
			}
		}
	}

	let keywordsRestrict: string[] = [];
	if (rule.filterKeywordsRestrict) {
		for (const keyword of rule.filterKeywordsRestrict) {
			if (keyword) {
				keywordsRestrict.push(keyword);
			}
		}
	}

	return {
		id: rule.id,
		ruleType: rule.ruleType,
		autoRecordRuleUsers: rule.autoRecordRuleUsers.map(autoRecordRuleUser => {
			if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
				return null;
			}

			return {
				user: {
					...autoRecordRuleUser.user,
				},
				includeAllReports: autoRecordRuleUser.includeAllReports,
			};
		}),
		filterOrganizer: rule.filterOrganizer,
		ruleLevel: rule.ruleLevel,
		filterAttendeeType: rule.filterAttendeeType,
		filterMinimumAttendees: rule.filterMinimumAttendees || undefined,
		filterMaximumAttendees: rule.filterMaximumAttendees || undefined,
		filterKeywordsContains: keywordsContains,
		filterKeywordsRestrict: keywordsRestrict,
	};
}

interface CreateAndEditModalProviderProps {
	children: React.ReactNode;
	close: () => void;
	refetch: () => void;
	account: CreateAndEditModalAccount$key;
	rule?: RuleInput;
	ruleType?: "RECORD" | "RESTRICT";
	ruleLevel?: AutoRecordRuleLevel;
}

function isAttendeesFilterInvalid(minimumAttendees?: number, maximumAttendees?: number) {
	const POSITIVE_SMALL_INTEGER_MAX = 32767;

	if ((minimumAttendees ?? 0) > POSITIVE_SMALL_INTEGER_MAX || (maximumAttendees ?? 0) > POSITIVE_SMALL_INTEGER_MAX) {
		return true;
	}

	if (maximumAttendees !== undefined && maximumAttendees < 2) {
		return true;
	}

	if (minimumAttendees !== undefined && minimumAttendees < 0) {
		return true;
	}

	// If both are defined, the filter means between min and max attendees
	if (minimumAttendees === undefined && maximumAttendees === undefined) {
		return false;
	}

	// If min is defined and max is not, the filter means at least min attendees
	if (minimumAttendees !== undefined && maximumAttendees === undefined) {
		return false;
	}

	// If max is defined and min is not, the filter means at most max attendees
	if (maximumAttendees !== undefined && minimumAttendees === undefined) {
		return false;
	}

	// If both are defined, the filter means between min and max attendees
	if (minimumAttendees !== undefined && maximumAttendees !== undefined) {
		return minimumAttendees > maximumAttendees || maximumAttendees - minimumAttendees <= 1; // The filter is inclusive of the min and max values so there needs to be a difference of 2 or more
	}

	return false;
}

function hasDuplicateUsersSelected(users: UserInput[]) {
	const selectedUserIds = users.map(user => user?.user?.id).filter(Boolean) as string[];

	return new Set(selectedUserIds).size !== selectedUserIds.length;
}

/**
 *
 * @param children the content of the modal
 * @param close a function that closes the modal
 * @param refetch a function that refetches the data after the mutation is complete
 * @param rule the rule object that is being edited
 * @param account the account object that the rule is associated with
 * @param ruleType the type of rule that is being created or edited
 * @returns a context provider that provides the state and functions to the CreateAndEditModal
 */
function CreateAndEditModalProvider({
	children,
	close,
	refetch,
	rule,
	account: accountRef,
	ruleType: inputType,
	ruleLevel: inputRuleLevel,
}: CreateAndEditModalProviderProps) {
	const isEditing = Boolean(rule?.id);

	const accountFragment = graphql`
		fragment CreateAndEditModalAccount on Account {
			id
			logo
			name
			...AutoRecordRulesListItemRuleDescriptionTooltips
		}
	`;

	const account = useFragment<CreateAndEditModalAccount$key>(accountFragment, accountRef);

	const [step, setStep] = useState(() => {
		if (isEditing || inputRuleLevel === "USER") {
			return CreationSteps.RULE_CONFIGURATION;
		}

		return CreationSteps.USER_SELECTION;
	});

	const [users, setUsers] = useState<UserInput[]>(rule?.autoRecordRuleUsers || []);
	const [attendeeType, setAttendeeType] = useState<FilterAttendeeType>(
		rule?.filterAttendeeType || "INTERNAL_AND_EXTERNAL",
	);

	const [filterMinimumAttendees, setFilterMinimumAttendees] = useState<number | undefined>(
		rule?.filterMinimumAttendees,
	);
	const [filterMaximumAttendees, setFilterMaximumAttendees] = useState<number | undefined>(
		rule?.filterMaximumAttendees,
	);
	const [filterKeywordsContains, setKeywordsToContain] = useState<string[]>(rule?.filterKeywordsContains || []);
	const [filterKeywordsRestrict, setKeywordsToRestrict] = useState<string[]>(rule?.filterKeywordsRestrict || []);
	const [ruleLevel, setRuleLevel] = useState<AutoRecordRuleLevel>(rule?.ruleLevel || inputRuleLevel || "USER");
	const [filterOrganizer, setFilterOrganizer] = useState<AutoRecordRuleFilterOrganizer>(
		rule?.filterOrganizer || "ANYONE",
	);

	const ruleType = rule?.ruleType || inputType || "RECORD";

	const addUserInput = useCallback((user: UserInput) => {
		setUsers(users => [...users, user]);
	}, []);

	const removeUserInput = useCallback((index: number) => {
		setUsers(users => {
			return users.filter((_, i) => i !== index);
		});
	}, []);

	const updateUserInput = useCallback((index: number, userInput: UserInput) => {
		setUsers(users => {
			return users.map((user, i) => {
				if (i === index) {
					return userInput;
				}
				return user;
			});
		});
	}, []);

	const closeModal = useCallback(() => {
		close();
	}, [close]);

	// Create a rule object that can be used to create or update a rule based on the current state of the modal
	const returnRule: RuleInput = useMemo(() => {
		const autoRecordRuleUsers = users.filter(Boolean) as UserInput[]; // Remove falsy values

		return {
			id: rule?.id,
			ruleType,
			autoRecordRuleUsers: autoRecordRuleUsers,
			ruleLevel: ruleLevel,
			filterAttendeeType: attendeeType,
			filterMinimumAttendees,
			filterMaximumAttendees,
			filterKeywordsContains,
			filterKeywordsRestrict,
			filterOrganizer,
		};
	}, [
		users,
		rule?.id,
		ruleType,
		ruleLevel,
		attendeeType,
		filterMinimumAttendees,
		filterMaximumAttendees,
		filterKeywordsContains,
		filterKeywordsRestrict,
		filterOrganizer,
	]);

	const updateRule = useUpdateRuleMutation(returnRule, refetch);
	const createRule = useCreateRuleMutation(returnRule, refetch);

	const nextStep = useCallback(() => {
		if (step === CreationSteps.USER_SELECTION) {
			if (!isEditing) {
				dispatchAutoRecordRuleAnalytics(
					{
						ruleType,
						ruleLevel,
					},
					"Clicked next auto-record restriction rule",
				);
			}
			setStep(CreationSteps.RULE_CONFIGURATION);
		} else {
			if (isEditing) {
				updateRule();
			} else {
				createRule();
			}
			closeModal();
		}
	}, [step, isEditing, ruleType, ruleLevel, closeModal, updateRule, createRule]);

	const previousStep = useCallback(() => {
		if (step === CreationSteps.RULE_CONFIGURATION) {
			setStep(CreationSteps.USER_SELECTION);
		}
	}, [step]);

	const value: CreateAndEditModalContext = useMemo(() => {
		const allKeywords = filterKeywordsContains.concat(filterKeywordsRestrict);
		const hasDuplicateKeywords = new Set(allKeywords).size !== allKeywords.length;

		// We declare this into a variable here for better type checking as there are quite a few properties that are required
		const value: CreateAndEditModalContext = {
			isEditing,
			step,
			nextStep,
			previousStep,
			addUserInput,
			removeUserInput,
			updateUserInput,
			closeModal,
			account,
			rule: returnRule,
			setAttendeeType,
			setFilterMinimumAttendees,
			setFilterMaximumAttendees,
			setFilterOrganizer,
			setKeywordsToContain,
			setKeywordsToRestrict,
			hasDuplicateKeywords,
			users,
			ruleLevel,
			setRuleLevel,
			setUsers,
		};

		return value;
	}, [
		filterKeywordsContains,
		filterKeywordsRestrict,
		isEditing,
		step,
		nextStep,
		previousStep,
		addUserInput,
		removeUserInput,
		updateUserInput,
		closeModal,
		account,
		returnRule,
		users,
		ruleLevel,
		setRuleLevel,
	]);

	return <CreateAndEditModalContext.Provider value={value}>{children}</CreateAndEditModalContext.Provider>;
}

function CreateAndEditModalBody() {
	const context = useCreateAndEditModal();

	switch (context.step) {
		case CreationSteps.USER_SELECTION:
			return <UserSelectionStep />;
		case CreationSteps.RULE_CONFIGURATION:
			return <RuleConfigurationStep />;
	}
}

function CreateAndEditModal() {
	const context = useCreateAndEditModal();

	const noUsersSelected = context.rule.autoRecordRuleUsers.length === 0;

	const isNextStepDisabled = useMemo(() => {
		switch (context.step) {
			case CreationSteps.USER_SELECTION:
				if (context.ruleLevel == null) {
					return true;
				} else if (context.ruleLevel == "GROUP" && noUsersSelected) {
					return true;
				}

				return hasDuplicateUsersSelected(context.users);
			case CreationSteps.RULE_CONFIGURATION:
				return (
					context.hasDuplicateKeywords ||
					isAttendeesFilterInvalid(context.rule.filterMinimumAttendees, context.rule.filterMaximumAttendees)
				);
		}
	}, [
		context.step,
		context.ruleLevel,
		context.users,
		context.hasDuplicateKeywords,
		context.rule.filterMinimumAttendees,
		context.rule.filterMaximumAttendees,
		noUsersSelected,
	]);

	return (
		<Modal onClose={noop}>
			<Modal.Header onClose={context.closeModal}>
				<Modal.Title>{context.rule.ruleType === "RECORD" ? "Recording rule" : "Recording restriction"}</Modal.Title>
			</Modal.Header>

			<Modal.Body dark>
				<CreateAndEditModalBody />
			</Modal.Body>

			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={context.closeModal}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={context.nextStep} disabled={isNextStepDisabled}>
					{context.step === CreationSteps.USER_SELECTION ? "Next" : context.isEditing ? "Save" : "Add rule"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

/**
 * Generic hook that returns a function that opens the CreateAndEditModal
 * @param args the required arguments to open the CreateAndEditModal
 * @returns a function that opens the CreateAndEditModal
 */
function useOpenCreateAndEditModal(args: CreateAndEditModalArgs) {
	const modals = useModals();
	const [isOpen, setIsOpen] = useState(false);

	const onClose = useCallback((close: () => void) => {
		return () => {
			setIsOpen(false);
			close();
		};
	}, []);

	const open = useCallback(() => {
		setIsOpen(true);
		modals.open(({ close }) => {
			return (
				<CreateAndEditModalProvider
					close={onClose(close)}
					rule={args.rule}
					ruleType={args.ruleType}
					refetch={args.refetch}
					account={args.account}
					ruleLevel={args.ruleLevel}
				>
					<CreateAndEditModal />
				</CreateAndEditModalProvider>
			);
		});
	}, [args.account, args.refetch, args.rule, args.ruleLevel, args.ruleType, modals, onClose]);

	return { open, isOpen };
}

/**
 * Hook that returns a fucntion that opens the CreateAndEditModal for either a restriction or record rule
 * Use this for when you want to create a new rule with default values
 * @param ruleType
 * @param refetch
 * @param account
 * @returns a function that opens the CreateAndEditModal
 */
export function useOpenCreateModal(
	ruleType: "RECORD" | "RESTRICT",
	refetch: () => void,
	account: CreateAndEditModalAccount$key,
	ruleLevel?: AutoRecordRuleLevel | null,
) {
	// We use workspace here as the default because the only case where it's null is when creating a workspace level rule
	// It can either be a group or workspace rule when ruleLevel is undefined, but in the case of collecting these analytics events
	// , we refer to these as workspace.
	dispatchAutoRecordRuleAnalytics(
		{ ruleType, ruleLevel: ruleLevel || "WORKSPACE" },
		`Clicked create auto record restriction rule`,
	);

	return useOpenCreateAndEditModal({
		ruleType,
		refetch,
		account,
		ruleLevel: ruleLevel || undefined,
	});
}

/**
 * Hook that returns a function that opens the CreateAndEditModal with the given rule
 * Use this hook when you want to edit a rule that exists in the database.
 * @param rule the rule object that is being edited
 * @param refetch a function that refetches the data after the mutation is complete
 * @param account the account object that the rule is associated with
 * @returns a function that opens the CreateAndEditModal with the given rule
 */
export function useOpenEditModal(
	rule: CreateAndEditModalRule$key,
	refetch: () => void,
	account: CreateAndEditModalAccount$key,
) {
	const ruleInput = useGetRuleInput(rule);

	return useOpenCreateAndEditModal({ rule: ruleInput, ruleType: ruleInput.ruleType, refetch, account });
}

/**
 * Hook that returns a function that opens the CreateAndEditModal with the given rule
 * Use this hook when you want to create a new rule with specified default values for the rule's configuration
 * See ExamplesList.tsx for an example of how to use this hook
 * @param rule
 * @param refetch
 * @param account
 * @returns
 */
export function useOpenRuleInputEditModal(
	rule: RuleInput,
	refetch: () => void,
	account: CreateAndEditModalAccount$key,
) {
	return useOpenCreateAndEditModal({ rule, ruleType: rule.ruleType, refetch, account });
}
